import React, { memo, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import useDiet from '../../lib/hooks/useDiet';
import { UserContext } from '../../lib/contexts';

const useStyles = makeStyles((theme) => ({
  desktopImage: {
    width: '190px',
    height: '234px',
    borderRadius: '8px',
    border: '2px solid #EFEFEF',
    objectFit: 'cover',
  },
  mobileImage: {
    width: '237px',
    height: '292px',
    borderRadius: '8px',
    border: '2px solid #EFEFEF',
    objectFit: 'cover',
  },
}));

/**
 * DietPatternImage - A reusable component for displaying diet pattern images
 *
 * @param {Object} props
 * @param {string} props.type - The diet type
 * @param {string} props.quality - The diet quality
 * @param {boolean} props.isMobile - Whether to apply mobile-specific styling
 * @param {Object} props.boxProps - Additional props to pass to the Box component
 * @param {Object} props.imageProps - Additional props to pass to the img element
 */
const DietPatternImageComponent = ({ type, quality, isMobile = false, boxProps = {}, imageProps = {} }) => {
  const classes = useStyles();
  const {
    state: { user },
  } = React.useContext(UserContext);

  let { isLoading, data } = useDiet(type, quality, user?.id);
  data = data?.data || data;

  const renderedImage = useMemo(() => {
    if (isLoading || !data?.fingerprintPhotoUrl) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            ...boxProps?.sx,
          }}
          {...boxProps}
        >
          <CircularProgress />
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          ...boxProps?.sx,
        }}
        {...boxProps}
      >
        <img
          src={data.fingerprintPhotoUrl}
          alt={data.name}
          className={isMobile ? classes.mobileImage : classes.desktopImage}
          {...imageProps}
        />
      </Box>
    );
  }, [
    isLoading,
    data?.fingerprintPhotoUrl,
    data?.name,
    isMobile,
    classes.mobileImage,
    classes.desktopImage,
    boxProps,
    imageProps,
  ]);

  return renderedImage;
};

DietPatternImageComponent.propTypes = {
  /** The diet type (e.g. 'DSH') */
  type: PropTypes.string.isRequired,
  /** The diet quality as a number or string (e.g. 10 or '10') */
  quality: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** Whether to apply mobile-specific styling */
  isMobile: PropTypes.bool,
  /** Additional props to pass to the Box component */
  boxProps: PropTypes.object,
  /** Additional props to pass to the img element */
  imageProps: PropTypes.object,
};

const DietPatternImage = memo(DietPatternImageComponent);

export default DietPatternImage;
